import {
  ContextVariables,
  TriggerEventStepComponent,
  TriggerEventStepContextVariable,
} from 'api/types';
import { useAppDispatch, useAppSelector } from 'hooks';
import React, { useEffect } from 'react';
import { postMessage } from '../../utils/postMessage';
import {
  cleanTriggerEvent,
  setTriggerEventExpectedContextVariables,
  setTriggerEventExpectedEventId,
  setTriggerEventExpectedEventName,
  setTriggerEventStatus,
} from 'actions/workflows/workflows';
import { selectConversationId, selectTriggerEvent } from 'reducers/workflows';
import { useUpdateConversation } from 'components/App/hooks';
import { selectIsCurrentTabPrimary } from 'persistence/persistenceBetweenTabs';
import { sendConversationAlert } from 'actions/workflows-v2/workflowsV2';

type TriggerEventStepProps = {
  setStepSent: (query?: string) => void;
  widgetComponent: TriggerEventStepComponent;
};

const TriggerEventStep: React.FC<TriggerEventStepProps> = ({
  setStepSent,
  widgetComponent,
}) => {
  const dispatch = useAppDispatch();
  const updateConversation = useUpdateConversation();
  const { component_fields: componentFields, isSent } = widgetComponent;
  const triggerEvent = useAppSelector(selectTriggerEvent);
  const isCurrentTabPrimary = useAppSelector(selectIsCurrentTabPrimary);
  const conversationId = useAppSelector(selectConversationId);

  // update store and post trigger event message
  useEffect(() => {
    if (!isSent && isCurrentTabPrimary) {
      dispatch(
        setTriggerEventExpectedContextVariables([
          ...componentFields.context_variables_to_update,
        ]),
      );
      dispatch(setTriggerEventExpectedEventId(componentFields.event_id));
      dispatch(setTriggerEventExpectedEventName(componentFields.event_name));
      dispatch(setTriggerEventStatus('in_progress'));
      postMessage({
        additionalContext: componentFields.additional_context,
        event: 'forethoughtTriggerEventAction',
        expectedContextVariables:
          componentFields.context_variables_to_update.map(cv => cv.name),
        name: componentFields.event_name,
        sentAt: Date.now(),
      });
      setStepSent();
    }
  }, [dispatch, isSent, setStepSent, componentFields, isCurrentTabPrimary]);

  // update conversation if everything is correct and event is in progress
  useEffect(() => {
    const generateUpdateContext = ({
      expectedContextVariables,
      payloadContextVariables,
    }: {
      expectedContextVariables: TriggerEventStepContextVariable[];
      payloadContextVariables: ContextVariables;
    }) => {
      const updateContext: ContextVariables = {};

      expectedContextVariables.forEach(cv => {
        const contextValue = payloadContextVariables[cv.name];

        if (contextValue === null || contextValue === undefined) {
          const description = `No context value found for '${cv.name}'`;
          dispatch(
            sendConversationAlert({
              alert_type: 'trigger-event-step',
              description: description,
              priority: 'P4',
              teams: ['Implementation Engineers'],
            }),
          );
          console.error(`TRIGGER EVENT ERROR - ${description}`);
        }

        updateContext[cv.id] =
          contextValue !== null && contextValue !== undefined
            ? contextValue
            : cv.default;
      });

      return updateContext;
    };

    const shouldUpdateConversation = () => {
      return (
        triggerEvent.triggerEventExpectedEventId === componentFields.event_id &&
        triggerEvent.triggerEventExpectedEventName ===
          componentFields.event_name &&
        triggerEvent.triggerEventStatus === 'in_progress' &&
        triggerEvent.triggerEventExpectedEventName ===
          triggerEvent.triggerEventPayload.eventName
      );
    };

    if (shouldUpdateConversation()) {
      updateConversation(
        generateUpdateContext({
          expectedContextVariables:
            triggerEvent.triggerEventExpectedContextVariables,
          payloadContextVariables:
            triggerEvent.triggerEventPayload.contextVariables,
        }),
      );
      dispatch(setTriggerEventStatus('complete'));
    }

    if (triggerEvent.triggerEventStatus === 'complete') {
      dispatch(cleanTriggerEvent());
    }
  }, [
    dispatch,
    triggerEvent,
    updateConversation,
    componentFields,
    conversationId,
  ]);

  return null;
};

export default TriggerEventStep;
